import React, { useState } from 'react';
import { 
  X, 
  ListTodo, 
  Target, 
  Calendar, 
  User, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  Plus,
  TrendingUp,
  Shield,
  Zap,
  Users
} from 'lucide-react';

interface CreateBacklogItemModalProps {
  insight: any;
  isOpen: boolean;
  onClose: () => void;
  onCreateBacklogItem: (backlogData: any) => void;
}

const CreateBacklogItemModal: React.FC<CreateBacklogItemModalProps> = ({ 
  insight, 
  isOpen, 
  onClose, 
  onCreateBacklogItem 
}) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    assignee: '',
    dueDate: '',
    estimatedEffort: 8,
    businessValue: 70,
    riskScore: 30,
    tags: [''],
    linkedInsight: insight?.id || '',
    strategicAlignment: 75,
    kpiImpact: [''],
    learningVelocity: 70,
    threatReadiness: 60,
    cycleTime: 10,
    opportunityScore: 70,
    complianceDrift: 20,
    teamCapability: 80,
    fraudRiskDefence: 40
  });

  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;

  React.useEffect(() => {
    if (insight && isOpen) {
      // Pre-populate form with insight data
      setFormData(prev => ({
        ...prev,
        title: `Implement ${insight.title}`,
        description: `Strategic initiative based on market insight: ${insight.description}`,
        businessValue: insight.impact === 'high' ? 90 : insight.impact === 'medium' ? 70 : 50,
        riskScore: insight.risks?.length * 15 || 30,
        tags: [insight.category, insight.type, 'market-driven'],
        linkedInsight: insight.id,
        strategicAlignment: insight.confidence || 75,
        kpiImpact: generateKPIImpact(insight),
        opportunityScore: insight.confidence || 70,
        threatReadiness: insight.type === 'threat' ? 90 : 60,
        dueDate: calculateDueDate(insight.timeframe)
      }));
    }
  }, [insight, isOpen]);

  const generateKPIImpact = (insight: any) => {
    const kpiMap = {
      'Technology': ['Innovation Index', 'System Performance', 'Technical Debt Reduction'],
      'Market': ['Market Share', 'Customer Acquisition', 'Revenue Growth'],
      'Competitive': ['Competitive Position', 'Market Differentiation', 'Brand Strength'],
      'Innovation': ['Innovation Index', 'R&D Efficiency', 'Patent Portfolio'],
      'Geographic': ['Market Expansion', 'Regional Revenue', 'Local Market Share'],
      'Regulatory': ['Compliance Rating', 'Risk Mitigation', 'Regulatory Readiness']
    };
    return kpiMap[insight?.category as keyof typeof kpiMap] || ['Business Performance'];
  };

  const calculateDueDate = (timeframe: string) => {
    const now = new Date();
    if (timeframe.includes('Immediate')) {
      now.setDate(now.getDate() + 7);
    } else if (timeframe.includes('1-3 months')) {
      now.setMonth(now.getMonth() + 2);
    } else if (timeframe.includes('3-6 months')) {
      now.setMonth(now.getMonth() + 4);
    } else if (timeframe.includes('6-12 months')) {
      now.setMonth(now.getMonth() + 8);
    } else {
      now.setMonth(now.getMonth() + 3);
    }
    return now.toISOString().split('T')[0];
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleArrayInputChange = (field: string, index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field as keyof typeof prev].map((item: string, i: number) => 
        i === index ? value : item
      )
    }));
  };

  const addArrayItem = (field: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field as keyof typeof prev], '']
    }));
  };

  const removeArrayItem = (field: string, index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field as keyof typeof prev].filter((_: any, i: number) => i !== index)
    }));
  };

  const handleSubmit = () => {
    // Generate backlog item ID
    const backlogId = `ADAPT-${Date.now().toString().slice(-6)}`;
    
    const backlogData = {
      id: backlogId,
      ...formData,
      status: 'todo',
      createdFrom: 'market-insight',
      sourceInsight: insight,
      createdAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      jiraId: backlogId
    };

    onCreateBacklogItem(backlogData);
    onClose();
  };

  const getStepIcon = (step: number) => {
    if (step < currentStep) return <CheckCircle className="w-5 h-5 text-green-400" />;
    if (step === currentStep) return <Clock className="w-5 h-5 text-indigo-400" />;
    return <div className="w-5 h-5 rounded-full border-2 border-gray-600"></div>;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-900 text-red-300 border-red-600';
      case 'high': return 'bg-orange-900 text-orange-300 border-orange-600';
      case 'medium': return 'bg-yellow-900 text-yellow-300 border-yellow-600';
      case 'low': return 'bg-green-900 text-green-300 border-green-600';
      default: return 'bg-gray-900 text-gray-300 border-gray-600';
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white mb-4">Basic Information</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                placeholder="Enter backlog item title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                placeholder="Describe the backlog item and its objectives"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Priority</label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleInputChange('priority', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                >
                  <option value="low">Low Priority</option>
                  <option value="medium">Medium Priority</option>
                  <option value="high">High Priority</option>
                  <option value="critical">Critical Priority</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Assignee</label>
                <input
                  type="text"
                  value={formData.assignee}
                  onChange={(e) => handleInputChange('assignee', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  placeholder="e.g., Sarah Chen"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Due Date</label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => handleInputChange('dueDate', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Estimated Effort (hours)</label>
                <input
                  type="number"
                  value={formData.estimatedEffort}
                  onChange={(e) => handleInputChange('estimatedEffort', parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  min="1"
                  max="200"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Cycle Time (days)</label>
                <input
                  type="number"
                  value={formData.cycleTime}
                  onChange={(e) => handleInputChange('cycleTime', parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  min="1"
                  max="90"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tags</label>
              {formData.tags.map((tag, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={tag}
                    onChange={(e) => handleArrayInputChange('tags', index, e.target.value)}
                    className="flex-1 px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                    placeholder="e.g., AI, Mobile, Security"
                  />
                  {formData.tags.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('tags', index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => addArrayItem('tags')}
                className="text-indigo-400 hover:text-indigo-300 text-sm flex items-center space-x-1"
              >
                <Plus className="w-4 h-4" />
                <span>Add Tag</span>
              </button>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white mb-4">Business Metrics</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Business Value (1-100)</label>
                <input
                  type="number"
                  value={formData.businessValue}
                  onChange={(e) => handleInputChange('businessValue', parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  min="1"
                  max="100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Risk Score (1-100)</label>
                <input
                  type="number"
                  value={formData.riskScore}
                  onChange={(e) => handleInputChange('riskScore', parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  min="1"
                  max="100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Strategic Alignment (1-100)</label>
                <input
                  type="number"
                  value={formData.strategicAlignment}
                  onChange={(e) => handleInputChange('strategicAlignment', parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  min="1"
                  max="100"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Learning Velocity (1-100)</label>
                <input
                  type="number"
                  value={formData.learningVelocity}
                  onChange={(e) => handleInputChange('learningVelocity', parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  min="1"
                  max="100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Threat Readiness (1-100)</label>
                <input
                  type="number"
                  value={formData.threatReadiness}
                  onChange={(e) => handleInputChange('threatReadiness', parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  min="1"
                  max="100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Team Capability (1-100)</label>
                <input
                  type="number"
                  value={formData.teamCapability}
                  onChange={(e) => handleInputChange('teamCapability', parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  min="1"
                  max="100"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Opportunity Score (1-100)</label>
                <input
                  type="number"
                  value={formData.opportunityScore}
                  onChange={(e) => handleInputChange('opportunityScore', parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  min="1"
                  max="100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Compliance Drift (1-100)</label>
                <input
                  type="number"
                  value={formData.complianceDrift}
                  onChange={(e) => handleInputChange('complianceDrift', parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  min="1"
                  max="100"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Fraud Risk Defence (1-100)</label>
                <input
                  type="number"
                  value={formData.fraudRiskDefence}
                  onChange={(e) => handleInputChange('fraudRiskDefence', parseInt(e.target.value))}
                  className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                  min="1"
                  max="100"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">KPI Impact</label>
              {formData.kpiImpact.map((kpi, index) => (
                <div key={index} className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={kpi}
                    onChange={(e) => handleArrayInputChange('kpiImpact', index, e.target.value)}
                    className="flex-1 px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-indigo-500 focus:outline-none"
                    placeholder="e.g., Customer Satisfaction, Revenue Growth"
                  />
                  {formData.kpiImpact.length > 1 && (
                    <button
                      onClick={() => removeArrayItem('kpiImpact', index)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={() => addArrayItem('kpiImpact')}
                className="text-indigo-400 hover:text-indigo-300 text-sm flex items-center space-x-1"
              >
                <Plus className="w-4 h-4" />
                <span>Add KPI</span>
              </button>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-semibold text-white mb-4">Review & Summary</h3>
            
            {/* Source Insight */}
            {insight && (
              <div className="bg-gray-700 rounded-lg p-4 mb-6">
                <h4 className="text-white font-medium mb-2">Created from Market Insight:</h4>
                <div className="text-sm text-indigo-400 font-medium">{insight.title}</div>
                <div className="text-xs text-gray-400 mt-1">
                  {insight.category} • {insight.type} • Confidence: {insight.confidence}%
                </div>
              </div>
            )}

            {/* Summary Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h4 className="text-white font-medium">Basic Information</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Title:</span>
                    <span className="text-white">{formData.title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Priority:</span>
                    <span className={`px-2 py-1 rounded-full text-xs border capitalize ${getPriorityColor(formData.priority)}`}>
                      {formData.priority}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Assignee:</span>
                    <span className="text-white">{formData.assignee || 'Unassigned'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Due Date:</span>
                    <span className="text-white">{new Date(formData.dueDate).toLocaleDateString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Estimated Effort:</span>
                    <span className="text-white">{formData.estimatedEffort}h</span>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="text-white font-medium">Strategic Metrics</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Business Value:</span>
                    <span className="text-green-400 font-bold">{formData.businessValue}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Risk Score:</span>
                    <span className="text-red-400 font-bold">{formData.riskScore}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Strategic Alignment:</span>
                    <span className="text-indigo-400 font-bold">{formData.strategicAlignment}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Learning Velocity:</span>
                    <span className="text-blue-400 font-bold">{formData.learningVelocity}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Team Capability:</span>
                    <span className="text-purple-400 font-bold">{formData.teamCapability}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* KPI Impact */}
            <div>
              <h4 className="text-white font-medium mb-3">KPI Impact ({formData.kpiImpact.filter(k => k).length})</h4>
              <div className="flex flex-wrap gap-2">
                {formData.kpiImpact.filter(kpi => kpi).map((kpi, index) => (
                  <span key={index} className="px-3 py-1 bg-indigo-900 text-indigo-300 rounded-full text-sm">
                    {kpi}
                  </span>
                ))}
              </div>
            </div>

            {/* Tags */}
            <div>
              <h4 className="text-white font-medium mb-3">Tags ({formData.tags.filter(t => t).length})</h4>
              <div className="flex flex-wrap gap-2">
                {formData.tags.filter(tag => tag).map((tag, index) => (
                  <span key={index} className="px-3 py-1 bg-gray-700 text-gray-300 rounded-full text-sm">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700 bg-gray-900 rounded-t-lg">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 px-3 py-2 rounded-lg bg-green-900 text-green-300 border border-green-600">
              <ListTodo className="w-5 h-5" />
              <span className="font-medium">Add to Backlog</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">From Market Insight</h2>
              <p className="text-gray-400 text-sm">{insight?.title}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colours p-2 rounded-lg hover:bg-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="p-6 border-b border-gray-700">
          <div className="flex items-center justify-between">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className="flex items-center space-x-2">
                  {getStepIcon(step)}
                  <span className={`text-sm font-medium ${
                    step <= currentStep ? 'text-white' : 'text-gray-500'
                  }`}>
                    Step {step}
                  </span>
                </div>
                {step < totalSteps && (
                  <div className={`w-16 h-0.5 mx-4 ${
                    step < currentStep ? 'bg-green-400' : 'bg-gray-600'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
          <div className="mt-2 text-xs text-gray-400">
            {currentStep === 1 && 'Define basic backlog item information'}
            {currentStep === 2 && 'Set business metrics and strategic alignment'}
            {currentStep === 3 && 'Review and finalize backlog item'}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {renderStepContent()}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-700 bg-gray-900 rounded-b-lg">
          <div className="text-sm text-gray-400">
            Step {currentStep} of {totalSteps}
          </div>
          <div className="flex items-center space-x-3">
            {currentStep > 1 && (
              <button
                onClick={() => setCurrentStep(currentStep - 1)}
                className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded-lg transition-colours"
              >
                Previous
              </button>
            )}
            {currentStep < totalSteps ? (
              <button
                onClick={() => setCurrentStep(currentStep + 1)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg transition-colours"
              >
                Next
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colours flex items-center space-x-2"
              >
                <ListTodo className="w-4 h-4" />
                <span>Add to Backlog</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateBacklogItemModal;